import cx from 'classnames';
import { forwardRef } from 'react';
import FloorPlanTable, {
  type TableHighlightingMode,
} from '@components/floorPlan/FloorPlanTable';
import type { TableIconCommonProps } from '@components/floorPlan/InteractiveFloorPlanTable';
import {
  TableTimer,
  type AssignedServer,
} from 'restaurantAdmin/floorPlans/apiHelpers';
import typography from '~styles/typography.scss';
import { ServerBadge } from '../sidePanel/servers/ServerBadge';
import { useReservationServiceContext } from '../state/ReservationServiceContext';
import styles from './UnoccupiedFloorPlanTable.scss';
import { ISOTimeTo12HourTime } from '@shared/utils/time';

export interface FloorPlanTableProps extends TableIconCommonProps {
  assignedServer?: AssignedServer | null;
  timer?: TableTimer | null;
  handleTableOnClick: React.MouseEventHandler<HTMLButtonElement>;
  isDisabled?: boolean;
  isHighlighted?: boolean;
  isSelected: boolean;
  tableHighlightingMode?: TableHighlightingMode;
}

export const UnoccupiedFloorPlanTable = forwardRef<
  HTMLDivElement,
  FloorPlanTableProps
>(
  (
    {
      assignedServer,
      timer,
      handleTableOnClick,
      iconName,
      iconWidthScale,
      isDisabled = false,
      isHighlighted,
      isSelected,
      left,
      name,
      orientation,
      tableHighlightingMode = 'outline',
      tableIconScale,
      testId,
      top,
    },
    ref,
  ) => {
    const buttonLabel = isHighlighted ? 'Unselect table' : 'Select table';

    const { shouldShowServerBadges, shouldShowTableNumbers, timerDisplayMode } =
      useReservationServiceContext();

    return (
      <FloorPlanTable
        left={left}
        top={top}
        tableIconScale={tableIconScale}
        data-testid={testId}
        ref={ref}
      >
        <button
          aria-label={buttonLabel}
          className={styles.button}
          data-tooltip-id={name}
          disabled={isDisabled}
          name={name}
          onClick={handleTableOnClick}
          type="button"
        >
          <FloorPlanTable.Icon
            iconName={iconName}
            iconWidthScale={iconWidthScale}
            isHighlighted={isHighlighted}
            orientation={orientation}
            tableHighlightingMode={tableHighlightingMode}
            tableIconScale={tableIconScale}
          />

          <div className={styles.overlay}>
            {assignedServer && shouldShowServerBadges && (
              <div className={styles.row}>
                <ServerBadge
                  variant="floorPlanYAxisStack"
                  serverName={assignedServer.name}
                  badgeColor={assignedServer.hexColor}
                />
              </div>
            )}

            {shouldShowTableNumbers && (
              <div
                className={cx(styles.row, typography.t3_12, styles.tableName)}
              >
                {name}
              </div>
            )}

            {timer && timerDisplayMode === 'countdown' && (
              <div className={cx(styles.row, typography.t3_12, styles.timer)}>
                {timer.timer}
              </div>
            )}
            {timer && timerDisplayMode === 'clockTime' && (
              <div className={cx(styles.row, typography.t3_12, styles.timer)}>
                {ISOTimeTo12HourTime(timer.upcomingReservation.time)}
              </div>
            )}
          </div>
        </button>
      </FloorPlanTable>
    );
  },
);

UnoccupiedFloorPlanTable.displayName = 'UnoccupiedFloorPlanTable';
